import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  Slider,
  Paper,
  Alert,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  MarkEmailRead as MarkReadIcon,
  Delete as DeleteIcon,
  Favorite as FavoriteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  PushPin as PushIcon
} from '@mui/icons-material';
import { notificationService, NotificationData, NotificationSettings } from '../services/NotificationService';
import { useAuth } from '../contexts/AuthContext';

interface NotificationCenterProps {
  anchorEl?: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ anchorEl, open, onClose }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [settings, setSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    reminderDays: 1
  });
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && open) {
      loadNotifications();
      loadSettings();
    }
  }, [user, open]);

  const loadNotifications = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const userNotifications = await notificationService.getUserNotifications(user.uid);
      setNotifications(userNotifications.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      
      const unread = await notificationService.getUnreadCount(user.uid);
      setUnreadCount(unread);
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    if (!user) return;
    
    try {
      const userSettings = await notificationService.getNotificationSettings(user.uid);
      setSettings(userSettings);
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await notificationService.markAsRead(notificationId);
    await loadNotifications();
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    
    await notificationService.markAllAsRead(user.uid);
    await loadNotifications();
  };

  const handleSettingsChange = async (field: keyof NotificationSettings, value: any) => {
    if (!user) return;
    
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    await notificationService.updateNotificationSettings(user.uid, newSettings);
  };

  const getNotificationIcon = (type: NotificationData['type']) => {
    switch (type) {
      case 'request_accepted':
        return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
      case 'request_rejected':
        return <CancelIcon sx={{ color: '#f44336' }} />;
      case 'request_sent':
        return <EmailIcon sx={{ color: '#2196f3' }} />;
      case 'return_reminder':
        return <TimeIcon sx={{ color: '#ff9800' }} />;
      case 'favorite_rented':
        return <FavoriteIcon sx={{ color: '#e91e63' }} />;
      case 'payment_success':
        return <PaymentIcon sx={{ color: '#4caf50' }} />;
      case 'damage_report':
        return <WarningIcon sx={{ color: '#ff5722' }} />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getNotificationColor = (type: NotificationData['type']) => {
    switch (type) {
      case 'request_accepted':
      case 'payment_success':
        return '#4caf50';
      case 'request_rejected':
      case 'damage_report':
        return '#f44336';
      case 'return_reminder':
        return '#ff9800';
      case 'favorite_rented':
        return '#e91e63';
      default:
        return '#2196f3';
    }
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return d.toLocaleDateString('fr-FR');
  };

  return (
    <>
      {/* Menu des notifications */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            mt: 1
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#a5816d',
          color: 'white',
          m: 0,
          p: 2
        }}>
          <Typography variant="h6">
            Notifications {unreadCount > 0 && `(${unreadCount})`}
          </Typography>
          <Box>
            <IconButton 
              size="small" 
              onClick={() => setShowSettings(true)}
              sx={{ color: 'white', mr: 1 }}
            >
              <SettingsIcon />
            </IconButton>
            <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {unreadCount > 0 && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<MarkReadIcon />}
                onClick={handleMarkAllAsRead}
                sx={{ borderColor: '#a5816d', color: '#a5816d' }}
              >
                Marquer tout comme lu
              </Button>
            </Box>
          )}

          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Chargement...</Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary">
                Aucune notification
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    sx={{
                      backgroundColor: notification.isRead ? 'transparent' : '#f8f9fa',
                      borderLeft: `4px solid ${getNotificationColor(notification.type)}`,
                      '&:hover': {
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                  >
                    <ListItemIcon>
                      {getNotificationIcon(notification.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: notification.isRead ? 'normal' : 'bold',
                            color: notification.isRead ? 'text.secondary' : 'text.primary'
                          }}
                        >
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {notification.message}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(notification.createdAt)}
                            </Typography>
                            {notification.metadata?.dressTitle && (
                              <Chip
                                label={notification.metadata.dressTitle}
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {!notification.isRead && (
                        <IconButton
                          size="small"
                          onClick={() => handleMarkAsRead(notification.id)}
                          sx={{ color: '#a5816d' }}
                        >
                          <MarkReadIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
      </Menu>

      {/* Dialog des paramètres */}
      <Dialog open={showSettings} onClose={() => setShowSettings(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#a5816d', color: 'white' }}>
          Paramètres de notifications
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Types de notifications
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.emailNotifications}
                  onChange={(e) => handleSettingsChange('emailNotifications', e.target.checked)}
                  sx={{ 
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#a5816d' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#a5816d' }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ mr: 1, color: '#2196f3' }} />
                  Notifications par email
                </Box>
              }
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smsNotifications}
                  onChange={(e) => handleSettingsChange('smsNotifications', e.target.checked)}
                  sx={{ 
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#a5816d' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#a5816d' }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SmsIcon sx={{ mr: 1, color: '#4caf50' }} />
                  Notifications par SMS
                </Box>
              }
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.pushNotifications}
                  onChange={(e) => handleSettingsChange('pushNotifications', e.target.checked)}
                  sx={{ 
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#a5816d' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#a5816d' }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PushIcon sx={{ mr: 1, color: '#ff9800' }} />
                  Notifications push
                </Box>
              }
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Rappel de retour
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Recevoir un rappel X jours avant la date de retour
          </Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={settings.reminderDays}
              onChange={(_, value) => handleSettingsChange('reminderDays', value)}
              min={0}
              max={7}
              step={1}
              marks={[
                { value: 0, label: '0j' },
                { value: 1, label: '1j' },
                { value: 3, label: '3j' },
                { value: 7, label: '7j' }
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value} jour${value > 1 ? 's' : ''}`}
              sx={{
                color: '#a5816d',
                '& .MuiSlider-thumb': { backgroundColor: '#a5816d' },
                '& .MuiSlider-track': { backgroundColor: '#a5816d' },
                '& .MuiSlider-rail': { backgroundColor: '#e0e0e0' }
              }}
            />
          </Box>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              Les notifications sont actuellement simulées. En production, elles seront envoyées 
              via email, SMS et push notifications.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettings(false)} sx={{ color: '#a5816d' }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationCenter;